import {
    CACHE_FRESHNESS_HEADER,
    CACHE_KEY_HEADER,
    CACHE_TAGS_HEADER,
    CACHE_TTL_HEADER
} from '@casumo/fabric-fundamentals';
import { TRACE_MFE_ORIGIN, TRACE_PARENT, TRACE_STATE } from './constants';

export const throwNotImplemented = (fnName: string) => {
    throw new Error(`Function ${fnName} is not implemented in gatekeeper legacy mode`);
};

type TCacheDataFromHeaders = {
    key?: string;
    ttl?: number;
    freshness?: number;
    tags?: string[];
};

export function getCacheHeadersValues(headers: Headers): TCacheDataFromHeaders {
    if (!headers) return {};

    const key = headers.get(CACHE_KEY_HEADER);
    if (!key) return {};

    const ttlFromCache = Number(headers.get(CACHE_TTL_HEADER)); // in seconds
    const ttl = isNaN(ttlFromCache) ? undefined : ttlFromCache * 1000; // in milliseconds
    const freshnessFromCache = Number(headers.get(CACHE_FRESHNESS_HEADER));
    let freshness = isNaN(freshnessFromCache) ? undefined : freshnessFromCache * 1000;
    const tags = (headers.get(CACHE_TAGS_HEADER)?.split(',') || []).map((s: string) => s.trim());

    if (freshness && ttl && freshness >= ttl) {
        freshness = undefined;
        console.error(
            `${key} Freshness (${freshness}) is greater than TTL (${ttl}). Unsetting freshness.`
        );
    }

    return {
        key,
        ttl,
        ...(freshness ? { freshness } : undefined),
        ...(tags.length ? { tags } : undefined)
    };
}

export function cleanUpCacheHeaders(headers: Headers): void {
    headers.delete(CACHE_KEY_HEADER);
    headers.delete(CACHE_TTL_HEADER);
    headers.delete(CACHE_TAGS_HEADER);
    headers.delete(CACHE_FRESHNESS_HEADER);
}

export function isInternalRequest(input: RequestInfo | URL): boolean {
    const url: string = input instanceof Request ? input.url : String(input);

    const isInternalAbsoluteRegex = /^(https?:)?\/\/(.*)?casumo(test)?.com/;
    const isInternalRelativeRegex = /^\/(?!\/).+/;
    const isInternalAbsoluteRequest = isInternalAbsoluteRegex.test(url);
    const isInternalRelativeRequest = isInternalRelativeRegex.test(url);

    return isInternalAbsoluteRequest || isInternalRelativeRequest;
}

function randomHex(length: number): string {
    const toHex = (buffer: ArrayLike<number>) =>
        Array.from(buffer)
            .map((b) => b.toString(16).padStart(2, '0'))
            .join('');

    return toHex(crypto.getRandomValues(new Uint8Array(length)));
}

function createTraceParent(): `${string}-${string}-${string}-${string}` {
    const version = '00'; // as hex
    const traceId = randomHex(16);
    const parentId = randomHex(8);
    const flags = '00'; // unsampled - as hex
    return `${version}-${traceId}-${parentId}-${flags}`;
}

export function setTraceHeaders(headers: Headers, origin: string = 'no-origin'): void {
    if (!headers.get(TRACE_PARENT)) {
        const traceparent = createTraceParent();
        headers.set(TRACE_PARENT, traceparent);
    }
    const traceState = headers.get(TRACE_STATE) || '';
    headers.set(TRACE_STATE, `${TRACE_MFE_ORIGIN}=${origin};${traceState}`);
}
